import { SCREENS, type ScreenId } from "./screens";

// Tour T-38 (owner, 21 October 2026): an event's fourteen sections were a wrapped row of
// links on its workspace page. They are four groups, shown as tabs on every screen that
// lives under an event, the active group's pages in a second row beneath.
//
// NAMED BY SCREEN ID, NOT BY ADDRESS. A path segment such as the attendance one is also a
// vocabulary value, and V28's sweep refuses it spelled here; the address comes from the
// route table (system/screens.ts), the one place it lives. Each page names the permission
// its own API refuses without, so a tab never leads a person to a refusal.
export type EventPage = { screen: ScreenId; label: string; requires: string };
export type EventGroup = { id: string; label: string; pages: readonly EventPage[] };

export const EVENT_GROUPS: readonly EventGroup[] = [
  {
    id: "plan",
    label: "Plan",
    pages: [
      { screen: "console.events.requirements", label: "Requirements", requires: "event.read" },
      { screen: "console.events.match", label: "Who to offer", requires: "event.read" },
      { screen: "console.events.offers", label: "Offers", requires: "event.read" },
      { screen: "console.events.crew", label: "Crew", requires: "event.read" },
    ],
  },
  {
    id: "live",
    label: "Live",
    pages: [
      { screen: "console.events.live", label: "Live ops", requires: "event.read" },
      { screen: "console.events.attendance", label: "Late and absent", requires: "event.read" },
      { screen: "console.events.incidents", label: "Incidents", requires: "incident.record" },
      { screen: "console.events.messages", label: "Messages", requires: "crew.message" },
      { screen: "console.events.timesheets", label: "Timesheets", requires: "event.read" },
    ],
  },
  {
    id: "money",
    label: "Money",
    pages: [
      { screen: "console.events.pay", label: "Pay", requires: "pay.read" },
      { screen: "console.events.expenses", label: "Expenses", requires: "expense.decide" },
      { screen: "console.events.invoicing", label: "Invoicing", requires: "finance.read" },
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    pages: [
      { screen: "console.events.risk", label: "Risk Radar", requires: "risk.read" },
      { screen: "console.events.risk-assessment", label: "Risk assessment", requires: "event.read" },
      { screen: "console.events.proof", label: "Proof Packs", requires: "compliance.read" },
    ],
  },
];

/** The event id when the path is a screen under one event, else null ("new" is not an event). */
export function eventIdOf(pathname: string): string | null {
  const m = /^\/console\/events\/([0-9a-f-]{36})(?:\/|$)/.exec(pathname);
  return m ? m[1]! : null;
}

export function eventPath(screen: ScreenId, eventId: string): string {
  const path = SCREENS.find((s) => s.id === screen)?.path ?? "";
  return `/console/${path.replace(":id", eventId)}`;
}

/** Which group's page the path is on: the page with the longest matching address. */
export function activeEventPage(pathname: string, eventId: string, groups: readonly EventGroup[]) {
  let best: { group: EventGroup; page: EventPage } | null = null;
  let bestLen = -1;
  for (const group of groups) {
    for (const page of group.pages) {
      const p = eventPath(page.screen, eventId);
      if ((pathname === p || pathname.startsWith(p + "/")) && p.length > bestLen) {
        best = { group, page };
        bestLen = p.length;
      }
    }
  }
  return best;
}
